import React, { useState, useEffect, useRef } from 'react';
import { useSwipeable } from '../../../../utils';
import './ResponsiveLayout.css';

// Simple animation utilities to replace framer-motion
const motion = {
  div: ({ children, style, className, ...props }) => (
    <div
      className={`${className || ''} motion-element`}
      style={{ ...style, transition: 'all 0.3s ease' }}
      {...props}
    >
      {children}
    </div>
  )
};

const AnimatePresence = ({ children }) => <div className="animate-presence">{children}</div>;

const ResponsiveLayout = ({ 
  pdfViewer, 
  signaturePanel,
  isMobile,
  onLayoutChange 
}) => {
  const [leftPanelWidth, setLeftPanelWidth] = useState(100); // PDF viewer width percentage
  const [signaturePanelVisible, setSignaturePanelVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [bottomSheetHeight, setBottomSheetHeight] = useState(0); // Mobile bottom sheet height
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false); // Mobile bottom sheet visibility
  
  const layoutRef = useRef();
  const resizeHandleRef = useRef();
  const bottomSheetRef = useRef();

  // Handle desktop panel resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !layoutRef.current) return;
      
      const rect = layoutRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
      
      if (newWidth >= 50 && newWidth <= 85) {
        setLeftPanelWidth(newWidth);
        onLayoutChange?.({ pdfWidth: newWidth, sidebarWidth: 100 - newWidth });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onLayoutChange]);

  // Mobile swipe handlers - currently unused
  // const swipeHandlers = useSwipeable({
  //   onSwipedUp: (eventData) => {
  //     if (isMobile && eventData.absY > 50) {
  //       setBottomSheetHeight(Math.min(bottomSheetHeight + 20, 80));
  //     }
  //   },
  //   onSwipedDown: (eventData) => {
  //     if (isMobile && eventData.absY > 50) {
  //       setBottomSheetHeight(Math.max(bottomSheetHeight - 20, 20));
  //     }
  //   },
  //   preventDefaultTouchmoveEvent: true,
  //   trackMouse: true
  // });

  // Desktop Layout
  if (!isMobile) {
    return (
      <div className="desktop-layout" ref={layoutRef}>
        {/* Main PDF Viewer Panel */}
        <div 
          className="pdf-panel"
          style={{ width: `${leftPanelWidth}%` }}
        >
          <div className="pdf-panel-header">
            <div className="document-title">
              <h2>vOffice Digital Sign</h2>
            </div>
            <div className="panel-actions">
            </div>
          </div>
          <div className="pdf-content">
            {pdfViewer}
          </div>
        </div>

        {/* Resize Handle */}
        <div 
          ref={resizeHandleRef}
          className="resize-handle"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
        >
          <div className="resize-indicator"></div>
        </div>


        {/* Floating Signature Panel */}
        <AnimatePresence>
          {signaturePanelVisible && (
            <motion.div 
              className="floating-signature-panel"
              initial={{ opacity: 0, scale: 0.9, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 50 }}
              transition={{ 
                type: "spring", 
                stiffness: 300, 
                damping: 30 
              }}
            >
              <div className="signature-panel-header">
                <h4>Signature Required</h4>
                <button 
                  className="close-floating-panel"
                  onClick={() => setSignaturePanelVisible(false)}
                >
                  ×
                </button>
              </div>
              <div className="signature-panel-content">
                {signaturePanel}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Floating Sign Here Button */}
        <div className="desktop-fab">
          <button
            className="sign-here-btn"
            onClick={() => setSignaturePanelVisible(!signaturePanelVisible)}
            title="Sign Here"
          >
            Sign Here
          </button>
        </div>
      </div>
    );
  }

  // Mobile Layout
  return (
    <div className="mobile-layout">
  
      {/* PDF Viewer with Compact Toolbar */}
      <div className="mobile-pdf-container">
        {/* Compact Mobile Toolbar */}

        {pdfViewer}
      </div>

      {/* Bottom Sheet */}
      {bottomSheetVisible && (
        <motion.div
          ref={bottomSheetRef}
          className="bottom-sheet"
          style={{ height: `${bottomSheetHeight}vh` }}
          drag="y"
          dragConstraints={{ top: -200, bottom: 200 }}
          onDragEnd={(_, info) => {
            const newHeight = bottomSheetHeight - (info.offset.y / window.innerHeight) * 100;
            const clamped = Math.max(0, Math.min(80, newHeight));
            setBottomSheetHeight(clamped);
            if (clamped === 0) {
              setBottomSheetVisible(false);
            }
          }}
        >
          <div className="bottom-sheet-handle">
            <div className="drag-indicator">
            <button
              className="close-floating-panel"
              onClick={() => {
                setBottomSheetVisible(false);
                setBottomSheetHeight(0);
              }}
            >
              ×
            </button>

            </div>
          </div>

          <div className="bottom-sheet-content">
            {/* Tab Navigation */}
            <div className="bottom-sheet-tabs">
              <button className="tab-button active">
                📝 Sign
              </button>
            </div>

            <div className="bottom-sheet-body">
              <AnimatePresence mode="wait">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  {signaturePanel}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}

      {/* Mobile FAB for quick actions */}
      <div className="mobile-fab">
        <button
          className="sign-here-btn"
          onClick={() => {
            setBottomSheetVisible(true);
            setBottomSheetHeight(80);
          }}
        >
          Sign Here
        </button>
      </div>
    </div>
  );
};

export default ResponsiveLayout;
